import React, { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { useSegmentsSlice } from '@gui/store/hooks';
import { getGrid } from 'store/LoadingStore';
import usePersistentStore, { useLayersBucket } from '@gui/store/persistent/persistentStore';
import { InfoCircle } from '@untitledui-pro/icons/line';
import { getSimTooltip, isVectorialScalarModeToggleEnabled } from 'Simulations/SimConfig';
import ResultSwitch from './ResultSwitch';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { formatFrameLabel } from './dataUtils';
import { ScalarModeIcon, VectorialModeIcon } from './vectorialScalarModeIcons';

type LayerVectorialScalarToggleProps = {
    layerId: string;
};

export const LayerVectorialScalarToggle: React.FC<LayerVectorialScalarToggleProps> = ({ layerId }) => {
    const mode = usePersistentStore((s) => s.layers.layerStates[layerId]?.VECTORIAL_SCALAR_MODE ?? 0);
    const updateLayerState = usePersistentStore((s) => s.updateLayerState);
    const simKind = useMemo(() => getGrid()?.searchLayerByUID?.(layerId)?.getSimKind?.(), [layerId]);
    const isEnabled = simKind != null ? isVectorialScalarModeToggleEnabled(simKind) : true;
    const tooltipLabel = mode === 0 ? 'Vectorial mode' : 'Scalar mode';

    return (
        <Box className="layer-mode-toggle">
            <CustomTooltip side="left" title={tooltipLabel}>
                <Box
                    className={`layer-mode-toggle__button${!isEnabled ? ' is-disabled' : ''}`}
                    component="button"
                    type="button"
                    data-testid="layer-mode-toggle"
                    disabled={!isEnabled}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (!isEnabled) return;
                        updateLayerState(layerId, { VECTORIAL_SCALAR_MODE: mode === 0 ? 1 : 0 });
                    }}
                    aria-label={tooltipLabel}
                >
                    {mode === 0 ? (
                        <VectorialModeIcon className="layer-mode-toggle__icon" />
                    ) : (
                        <ScalarModeIcon className="layer-mode-toggle__icon" />
                    )}
                </Box>
            </CustomTooltip>
        </Box>
    );
};

export const LayerInfoIndicator: React.FC = () => {
    const { segments } = useSegmentsSlice();
    const activeLayerID = usePersistentStore((s) => s.layers.activeLayerID);

    const framesInfo = useMemo(() => {
        const layer = getGrid()?.getActiveLayer?.();
        const simKind = layer?.getSimKind();
        const frames = layer?.getFrameCollection?.() ?? [];
        return { max: frames.length, simKind };
    }, [activeLayerID]);

    const frameCount = Math.max(framesInfo.max ?? 0, segments?.frameMax ?? 0);
    const tooltipMessage = useMemo(() => getSimTooltip(framesInfo.simKind, frameCount), [frameCount, framesInfo.simKind]);

    if (tooltipMessage === undefined) return null;

    return (
        <CustomTooltip side="left" title={tooltipMessage}>
            <InfoCircle className="icon-size-small" />
        </CustomTooltip>
    );
};

type FrameSelectedBadgeProps = {
    isOpen: boolean;
    onToggle: () => void;
};

const FrameSelectedBadgeComponent: React.FC<FrameSelectedBadgeProps> = ({ isOpen, onToggle }) => {
    const { segments } = useSegmentsSlice();
    const frameSelected = segments?.frameSelected;
    const activeLayerID = useLayersBucket((s) => s.activeLayerID);

    const framesInfo = useMemo(() => {
        const layer = getGrid()?.getActiveLayer?.();
        let simKind = layer?.getSimKind();
        const frames = layer?.getFrameCollection?.() ?? [];
        const names = frames.map((f: any, i: number) => f?.name ?? `Frame ${i + 1}`);
        return { names, max: frames.length, simKind };
    }, [activeLayerID]);

    const frameCount = Math.max(framesInfo.max ?? 0, segments?.frameMax ?? 0);

    return (
        <Box className="layer-selected-row">
            {frameCount > 1 && segments && (
                <Button
                    className="layer-selected-row__badge"
                    variant={isOpen ? 'contained' : 'outlined'}
                    size="small"
                    onClick={(event) => {
                        event.stopPropagation();
                        onToggle();
                    }}
                >
                    {!segments.isAggregate
                        ? (() => {
                              const label = formatFrameLabel(frameSelected, framesInfo);
                              if (typeof label === 'string' && frameCount === 12 && label.length > 3) return label.slice(0, 3);
                              return label;
                          })()
                        : 'Agg'}
                </Button>
            )}
            <ResultSwitch></ResultSwitch>
        </Box>
    );
};

const FrameSelectedBadge = React.memo(FrameSelectedBadgeComponent);

export type { FrameSelectedBadgeProps };
export default FrameSelectedBadge;
